import { Spin } from 'antd';
import { orderCODSum } from 'components/pages/dashboard/ParcelOrder/components/parcelOrderFunctions';
import TcLoading from 'components/UI/loading/TcLoading';
import { separator } from 'global/default';
import { packageCreate } from 'global/Models/omOrderModel';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

export type priceTypeBranch = {
  packNum: number;
  originCity: string;
  destiationCity: string;
  regularRate: {
    serviceId: number;
    serviceTitle: string;
    transportationCost: number;
    compensation: number;
    noticeOfDelivery: number;
    tracking: number;
    packing: number;
    beforeTaxAndDiscount: number;
    tax: number;
    finalPrice: number;
    discount: number;
  } | null;
  regularPlusRate: any | null;
  expressRate: any | null;
  sameDayExpressRate: any | null;
  airExpressRate: any | null;
};

interface IParcelOrderPrice {
  priceList?: priceTypeBranch;
  packages: packageCreate[];
  loading: boolean;
  haveCOD?: boolean;
}

const BranchParcelOrderPrice: FC<IParcelOrderPrice> = ({ priceList, packages, loading, haveCOD = false }) => {
  // states
  const [isWrapperInView, setIsWrapperInView] = useState(false);

  //hooks
  const wrapperDivRef = useRef<HTMLDivElement>(null);

  //effects
  useEffect(() => {
    const handleScroll = () => {
      setIsWrapperInView(getIsWrapperInView());
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  //functions
  const getIsWrapperInView = () => {
    if (wrapperDivRef.current) {
      const rect = wrapperDivRef.current.getBoundingClientRect();
      return rect.top >= 0 && rect.bottom <= window.innerHeight;
    }
    return false;
  };

  //constants
  // Adapted for new data model: priceList is a single object, not an array
  const orderSum = useMemo(() => {
    if (!priceList) return 0;
    // Use regularRate.finalPrice if available, otherwise 0
    return priceList.regularRate?.finalPrice ?? 0;
  }, [priceList]);

  const codSum = useMemo(() => orderCODSum(packages), [packages]);

  const totalPrice = useMemo(() => {
    if (!priceList) return 0;
    return haveCOD ? orderSum + codSum : orderSum;
  }, [orderSum, codSum, haveCOD]);

  if (loading)
    return (
      <>
        <div className=' text-t-secondary-text-color'>
          <Spin spinning size='small' /> درحال بارگزاری مبلغ سفارش...
        </div>
        {!isWrapperInView && (
          <div className='fixed z-50 flex flex-col p-2 text-xs border rounded-lg shadow-md left-4 bottom-2 border-t-primary-color bg-t-bg-color color-inherit'>
            <span className='text-sm'>
              <TcLoading spinning size='small' className='ml-1' />
              در حال محاسبه هزینه...
            </span>
          </div>
        )}
      </>
    );

  if (!priceList) return <></>;

  return (
    <>
      <div className='flex flex-col' ref={wrapperDivRef}>
        <span className='text-xs text-t-secondary-text-color'>
          مبدا: {priceList.originCity} | مقصد: {priceList.destiationCity}
        </span>
        {haveCOD && (
          <>
            <span className='text-xs text-t-secondary-text-color'>هزینه ارسال : {separator(orderSum?.toFixed(0))} ریال</span>
            <span className='text-xs text-t-secondary-text-color'>ارزش مرسوله : {separator(codSum?.toFixed(0))} ریال</span>
          </>
        )}
        <span>مجموع : {separator(totalPrice.toFixed(0))} ریال</span>
      </div>
      {!isWrapperInView && (
        <div className='fixed z-50 flex flex-col p-2 border rounded-lg shadow-md left-4 bottom-2 border-t-primary-color bg-t-bg-color color-inherit'>
          <span className='text-xs text-t-secondary-text-color'>
            مبدا: {priceList.originCity} | مقصد: {priceList.destiationCity}
          </span>
          {haveCOD && (
            <div className='flex flex-col'>
              <p className='text-xs !text-t-secondary-text-color'>هزینه ارسال : {separator(orderSum?.toFixed(0))} ریال</p>
              <p className='text-xs !text-t-secondary-text-color'>ارزش مرسوله : {separator(codSum?.toFixed(0))} ریال</p>
            </div>
          )}
          <span className='text-sm font-medium'>مجموع : {separator(totalPrice.toFixed(0))} ریال</span>
        </div>
      )}
    </>
  );
};

export default BranchParcelOrderPrice;
